import React, { useCallback, useEffect, useState } from 'react';
import {
  Table, Tag, Button, Space, Select, Row, Col, Tooltip, Popconfirm,
  Modal, Form, Input, InputNumber, Alert, Divider,
} from 'antd';
import {
  PlusOutlined, ReloadOutlined, KeyOutlined, StopOutlined,
  PlayCircleOutlined, SyncOutlined, EyeOutlined, CopyOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import Layout from '../../components/Layout';
import {
  listerClesParCompagnie, creerCleApi, revoquerCle,
  suspendreCle, reactiverCle, regenererSecret,
  STATUTS_CLE_API, ENVIRONNEMENTS,
} from '../../services/cleApiService';
import { listerOrganismesCompagnies } from '../../services/attestationsService';
import PageHeader from '../../components/PageHeader';

const { Option } = Select;

export default function ClesApiPage() {
  const { t } = useTranslation('clesApi');
  const [compagnies, setCompagnies] = useState([]);
  const [selectedCompagnie, setSelectedCompagnie] = useState(
    () => sessionStorage.getItem('clesapi_compagnie') || null
  );
  const [cles, setCles] = useState([]);
  const [loading, setLoading] = useState(false);

  const [modalCreer, setModalCreer] = useState(false);
  const [secretModal, setSecretModal] = useState(null);

  useEffect(() => {
    listerOrganismesCompagnies()
      .then(setCompagnies)
      .catch(() => toast.error(i18n.t('clesApi:messages.chargementCompagniesImpossible')));
  }, []);

  const load = useCallback(async () => {
    if (!selectedCompagnie) { setCles([]); return; }
    setLoading(true);
    try {
      const list = await listerClesParCompagnie(selectedCompagnie);
      setCles(list);
    } catch {
      toast.error(i18n.t('clesApi:messages.erreurChargementCles'));
    } finally {
      setLoading(false);
    }
  }, [selectedCompagnie]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (action, trackingId, label) => {
    try {
      let result;
      if (action === 'revoquer') result = await revoquerCle(trackingId);
      else if (action === 'suspendre') result = await suspendreCle(trackingId);
      else if (action === 'reactiver') result = await reactiverCle(trackingId);
      else if (action === 'regenerer') {
        result = await regenererSecret(trackingId);
        setSecretModal(result);
      }
      if (action !== 'regenerer') toast.success(t('messages.actionReussie', { label }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('messages.actionEchouee', { label }));
    }
  };

  const columns = [
    {
      title: t('colonnes.integration'),
      dataIndex: 'nomIntegration',
      key: 'nom',
      render: (val, rec) => (
        <div>
          <div style={{ fontWeight: 600 }}>{val}</div>
          <div style={{ fontSize: 11, color: '#6b7280', fontFamily: 'var(--cb-font-mono)' }}>
            {rec.cleApi}
          </div>
        </div>
      ),
    },
    {
      title: t('colonnes.environnement'),
      dataIndex: 'environnement',
      key: 'env',
      width: 110,
      render: (val) => {
        const e = ENVIRONNEMENTS[val] || { label: val, color: 'default' };
        return <Tag color={e.color}>{e.label}</Tag>;
      },
    },
    {
      title: t('colonnes.statut'),
      dataIndex: 'statut',
      key: 'statut',
      width: 100,
      render: (val) => {
        const s = STATUTS_CLE_API[val] || { label: val, color: 'default' };
        return <Tag color={s.color}>{s.label}</Tag>;
      },
    },
    {
      title: t('colonnes.quota'),
      key: 'quota',
      width: 110,
      render: (_, rec) => (
        <span style={{ fontSize: 12 }}>
          {rec.appelsAujourdhui}/{rec.quotaJournalier} {t('colonnes.jour')}
          <br />
          <span style={{ color: '#6b7280' }}>
            {rec.appelsCeMois}/{rec.quotaMensuel} {t('colonnes.mois')}
          </span>
        </span>
      ),
    },
    {
      title: t('colonnes.expiration'),
      dataIndex: 'dateExpiration',
      key: 'expiration',
      width: 110,
      render: (val) => val || <span style={{ color: '#9ca3af' }}>{t('colonnes.aucuneExpiration')}</span>,
    },
    {
      title: t('colonnes.actions'),
      key: 'actions',
      width: 160,
      render: (_, rec) => (
        <Space size={4}>
          {rec.statut === 'ACTIVE' && (
            <Tooltip title={t('actions.suspendre')}>
              <Popconfirm title={t('actions.suspendreConfirmation')} onConfirm={() => handleAction('suspendre', rec.trackingId, t('actions.suspendue'))}>
                <Button size="small" icon={<StopOutlined />} />
              </Popconfirm>
            </Tooltip>
          )}
          {rec.statut === 'SUSPENDUE' && (
            <Tooltip title={t('actions.reactiver')}>
              <Button size="small" icon={<PlayCircleOutlined />}
                onClick={() => handleAction('reactiver', rec.trackingId, t('actions.reactivee'))} />
            </Tooltip>
          )}
          {rec.statut !== 'REVOQUEE' && (
            <Tooltip title={t('actions.regenererSecret')}>
              <Popconfirm title={t('actions.regenererSecretConfirmation')} onConfirm={() => handleAction('regenerer', rec.trackingId, t('actions.secretRegenere'))}>
                <Button size="small" icon={<SyncOutlined />} />
              </Popconfirm>
            </Tooltip>
          )}
          {rec.statut !== 'REVOQUEE' && (
            <Tooltip title={t('actions.revoquer')}>
              <Popconfirm title={t('actions.revoquerConfirmation')} onConfirm={() => handleAction('revoquer', rec.trackingId, t('actions.revoquee'))} okButtonProps={{ danger: true }}>
                <Button size="small" danger icon={<KeyOutlined />} />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Layout title={t('page.titreOnglet')}>
      {/* Les cles ne sont chargees qu'une fois une compagnie choisie : tant qu'aucune
          ne l'est, afficher « 0 cle » ferait croire a une compagnie sans cle. */}
      <PageHeader
        titre={t('page.titre')}
        icone={<KeyOutlined />}
        compte={selectedCompagnie ? cles.length : undefined}
        unite={t('page.unite')}
        unitePluriel={t('page.unitePluriel')}
        actions={(
          <Button
            type="primary" icon={<PlusOutlined />}
            disabled={!selectedCompagnie}
            style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}
            onClick={() => setModalCreer(true)}>
            {t('actions.nouvelleCle')}
          </Button>
        )}
      />

      <div className="cb-card" style={{ padding: 16 }}>
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={12}>
            <Select
              placeholder={t('filtres.selectionnerCompagnie')}
              showSearch optionFilterProp="children"
              style={{ width: '100%' }}
              value={selectedCompagnie}
              onChange={(v) => {
                setSelectedCompagnie(v);
                if (v) sessionStorage.setItem('clesapi_compagnie', v);
                else sessionStorage.removeItem('clesapi_compagnie');
              }}>
              {compagnies.map((c) => (
                <Option key={c.organismeTrackingId || c.id} value={c.organismeTrackingId || c.id}>
                  {c.raisonSociale}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={4}>
            <Button icon={<ReloadOutlined />} onClick={load} disabled={!selectedCompagnie} block>
              {t('actions.actualiser')}
            </Button>
          </Col>
        </Row>

        {!selectedCompagnie ? (
          <Alert
            type="info"
            message={t('filtres.aucuneCompagnie')}
            style={{ borderRadius: 8 }}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={cles}
            rowKey="trackingId"
            loading={loading}
            pagination={false}
            size="small"
          />
        )}
      </div>

      <CreerCleModal
        open={modalCreer}
        compagnieId={selectedCompagnie}
        onClose={() => setModalCreer(false)}
        onCreated={(result) => {
          setModalCreer(false);
          if (result?.secretClair) {
            setSecretModal(result);
          }
          load();
        }}
      />

      <SecretModal data={secretModal} onClose={() => setSecretModal(null)} />
    </Layout>
  );
}

function CreerCleModal({ open, compagnieId, onClose, onCreated }) {
  const { t } = useTranslation('clesApi');
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) form.resetFields();
  }, [open, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const result = await creerCleApi({ ...values, compagnieId });
      toast.success(t('creation.succes'));
      onCreated(result);
    } catch (e) {
      if (e?.errorFields) return;
      toast.error(e?.response?.data?.message || t('creation.erreur'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={t('creation.titre')}
      open={open} onCancel={onClose} width={520}
      footer={
        <Space>
          <Button onClick={onClose}>{t('creation.annuler')}</Button>
          <Button type="primary" loading={loading} onClick={handleSubmit}
            style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}>
            {t('creation.creer')}
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item name="nomIntegration" label={t('creation.nomIntegration')} rules={[{ required: true }]}>
          <Input placeholder={t('creation.nomIntegrationExemple')} />
        </Form.Item>
        <Form.Item name="environnement" label={t('creation.environnement')} initialValue="PRODUCTION">
          <Select>
            <Option value="PRODUCTION">{t('creation.environnementProduction')}</Option>
            <Option value="SANDBOX">{t('creation.environnementSandbox')}</Option>
          </Select>
        </Form.Item>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="quotaJournalier" label={t('creation.quotaJournalier')} initialValue={500}>
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="quotaMensuel" label={t('creation.quotaMensuel')} initialValue={10000}>
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="description" label={t('creation.description')}>
          <Input.TextArea rows={2} placeholder={t('creation.descriptionExemple')} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function SecretModal({ data, onClose }) {
  const { t } = useTranslation('clesApi');
  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text).then(() => toast.success(t('secret.copie')));
  };

  return (
    <Modal
      title={t('secret.titre')}
      open={!!data}
      onCancel={onClose}
      footer={<Button type="primary" onClick={onClose} style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}>{t('secret.jaiNote')}</Button>}
      closable={false}
    >
      <Alert
        type="warning"
        message={t('secret.avertissement')}
        style={{ marginBottom: 16 }}
      />
      <Divider orientation="left" style={{ fontSize: 12 }}>{t('secret.clePublique')}</Divider>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Input value={data?.cleApi} readOnly style={{ fontFamily: 'var(--cb-font-mono)', fontSize: 13 }} />
        <Button icon={<CopyOutlined />} onClick={() => copyToClipboard(data?.cleApi)} />
      </div>
      <Divider orientation="left" style={{ fontSize: 12 }}>{t('secret.secret')}</Divider>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Input.Password
          value={data?.secretClair}
          readOnly
          style={{ fontFamily: 'var(--cb-font-mono)', fontSize: 13 }}
        />
        <Button icon={<CopyOutlined />} onClick={() => copyToClipboard(data?.secretClair)} />
      </div>
    </Modal>
  );
}
